import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  apriLinea, chatPassata, chatPassate, miaChat, nonLetti, scrivi, segnaLetta,
} from '../../services/assistenzaApi';
import './ChatAssistenza.css';

/**
 * LA BOLLA DELLA CHAT DI ASSISTENZA, in basso a destra su tutte le pagine.
 *
 * C'e' solo per chi e' entrato col suo conto, e solo se la radice l'ha
 * accesa nel backoffice (Sito › Messaggistica › Chat assistenza ›
 * Impostazioni). Il cliente scrive; il messaggio arriva agli operatori nel
 * backoffice e sul loro bot Telegram; la risposta torna qui.
 *
 * Chiusa, la bolla legge solo il numero delle risposte non viste, a tempo.
 * Aperta, tiene la linea in tempo reale col postino (e rilegge a tempo lo
 * stesso, piu' piano, se la linea cade).
 *
 * Il nome dell'operatore non si vede: risponde «l'assistenza».
 */

const OGNI_CHIUSA_MS = 60 * 1000;
const OGNI_IN_ATTESA_MS = 20 * 1000;
const OGNI_APERTA_MS = 15 * 1000;
const TESTO_MAX = 2000;

const entrato = () => !!window.localStorage.getItem('access_token');

function ora(iso, lingua) {
  if (!iso) return '';
  const d = new Date(iso);
  const oggi = new Date().toDateString() === d.toDateString();
  try {
    return oggi
      ? d.toLocaleTimeString(lingua, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleString(lingua, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return d.toLocaleString();
  }
}

export default function ChatAssistenza() {
  const { t, i18n } = useTranslation();
  const posizione = useLocation();
  const [dentro, setDentro] = useState(entrato());
  const [accesa, setAccesa] = useState(false);
  const [numero, setNumero] = useState(0);
  const [aperta, setAperta] = useState(false);
  const [chat, setChat] = useState(null);
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState(null);
  const [passate, setPassate] = useState(null);
  const [passata, setPassata] = useState(null);
  const [adesso, setAdesso] = useState(Date.now());
  const fondo = useRef(null);
  const campo = useRef(null);

  /* Entrare o uscire cambia il gettone: si guarda a ogni cambio di pagina e
     quando cambia in un'altra scheda. */
  useEffect(() => { setDentro(entrato()); }, [posizione.pathname]);
  useEffect(() => {
    const f = (e) => { if (!e || e.key === 'access_token' || e.key === null) setDentro(entrato()); };
    window.addEventListener('storage', f);
    return () => window.removeEventListener('storage', f);
  }, []);

  const inAttesa = !!(chat && chat.conversazione && chat.conversazione.stato !== 'CHIUSA');

  /* IL NUMERO SULLA BOLLA, a chat chiusa. */
  useEffect(() => {
    if (!dentro || aperta) return undefined;
    let vivo = true;
    const leggi = async () => {
      try {
        const r = await nonLetti();
        if (!vivo) return;
        setAccesa(!!r.accesa);
        setNumero(r.nonLetti || 0);
      } catch (e) {
        if (vivo && e && e.response && (e.response.status === 401 || e.response.status === 403)) setAccesa(false);
      }
    };
    leggi();
    const orologio = setInterval(leggi, inAttesa ? OGNI_IN_ATTESA_MS : OGNI_CHIUSA_MS);
    return () => { vivo = false; clearInterval(orologio); };
  }, [dentro, aperta, inAttesa]);

  const rileggi = useCallback(async () => {
    try {
      const r = await miaChat();
      setChat(r);
      setAccesa(!!r.accesa);
      setErrore(null);
      const conv = r.conversazione;
      if (conv && conv.nonLetti > 0) {
        segnaLetta().catch(() => {});
      }
      setNumero(0);
    } catch (e) {
      setErrore(t('La chat non si carica. Riprova fra poco.'));
    }
  }, [t]);

  /* APERTA: si legge subito, la linea in tempo reale, e un giro lento sotto. */
  useEffect(() => {
    if (!aperta || !dentro) return undefined;
    rileggi();
    const chiudiLinea = apriLinea(() => rileggi());
    const orologio = setInterval(rileggi, OGNI_APERTA_MS);
    return () => { chiudiLinea(); clearInterval(orologio); };
  }, [aperta, dentro, rileggi]);

  /* L'orologio della frase «ti risponderemo appena possibile». */
  useEffect(() => {
    if (!aperta) return undefined;
    const o = setInterval(() => setAdesso(Date.now()), 30 * 1000);
    return () => clearInterval(o);
  }, [aperta]);

  const quanti = chat && chat.messaggi ? chat.messaggi.length : 0;
  useEffect(() => {
    if (fondo.current && fondo.current.scrollIntoView) fondo.current.scrollIntoView({ block: 'end' });
  }, [quanti, aperta, passata]);

  useEffect(() => {
    if (!aperta) return undefined;
    const tasto = (e) => { if (e.key === 'Escape') setAperta(false); };
    window.addEventListener('keydown', tasto);
    if (campo.current) campo.current.focus();
    return () => window.removeEventListener('keydown', tasto);
  }, [aperta]);

  /* Sul telefono, a chat aperta, la pagina sotto non scorre. */
  useEffect(() => {
    if (!aperta) return undefined;
    document.body.classList.add('chat-ass-aperta');
    return () => document.body.classList.remove('chat-ass-aperta');
  }, [aperta]);

  if (!dentro || !accesa) return null;

  async function manda(e) {
    if (e) e.preventDefault();
    const t0 = testo.trim();
    if (!t0 || invio) return;
    setInvio(true);
    try {
      await scrivi(t0);
      setTesto('');
      await rileggi();
    } catch (err) {
      const d = err && err.response && err.response.data;
      setErrore(d && d.code === 'TROPPI_MESSAGGI'
        ? t('Stai scrivendo troppo in fretta: aspetta un momento.')
        : d && d.code === 'CHAT_SPENTA'
          ? t('La chat di assistenza in questo momento non è attiva.')
          : t('Messaggio non inviato. Riprova.'));
    }
    setInvio(false);
  }

  async function apriPassate() {
    setPassata(null);
    try { setPassate(await chatPassate()); } catch (e) { setPassate([]); }
  }

  async function apriPassata(id) {
    try { setPassata(await chatPassata(id)); } catch (e) { setPassata(null); }
  }

  const conv = chat && chat.conversazione;
  const messaggi = (chat && chat.messaggi) || [];
  const chiusa = conv && conv.stato === 'CHIUSA';
  const ultimo = messaggi.length ? messaggi[messaggi.length - 1] : null;
  const attesaMs = ((chat && chat.attesaMinuti) || 3) * 60 * 1000;
  const senzaRisposta = conv && !chiusa && conv.ultimoDa === 'CLIENTE' && ultimo
    && adesso - new Date(ultimo.il).getTime() > attesaMs;

  const fumetto = (m) => {
    if (m.da === 'SISTEMA') {
      const cosa = m.evento === 'PRESA'
        ? t('Un operatore ha preso in carico la tua richiesta')
        : m.evento === 'CHIUSA' ? t('La chat è stata chiusa') : null;
      if (!cosa) return null;
      return <div key={m.id} className="chat-ass-sistema">{cosa}</div>;
    }
    const mio = m.da === 'CLIENTE';
    return (
      <div key={m.id} className={'chat-ass-riga ' + (mio ? 'mia' : 'loro')}>
        <div className="chat-ass-fumetto">
          {!mio && <span className="chat-ass-chi">{t('Assistenza')}</span>}
          <span className="chat-ass-testo">{m.testo}</span>
          <span className="chat-ass-ora">{ora(m.il, i18n.language)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-ass">
      {aperta && (
        <section className="chat-ass-pannello" role="dialog" aria-label={t('Chat di assistenza')}>
          <header className="chat-ass-testa">
            {passate ? (
              <button type="button" className="chat-ass-icona" aria-label={t('Indietro')}
                      onClick={() => (passata ? setPassata(null) : setPassate(null))}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
            ) : null}
            <div className="chat-ass-titolo">
              <strong>{passate ? t('Chat precedenti') : t('Assistenza')}</strong>
              {!passate && <span>{t('Scrivici qui, ti rispondiamo in questa chat.')}</span>}
            </div>
            {!passate && (
              <button type="button" className="chat-ass-link" onClick={apriPassate}>{t('Chat precedenti')}</button>
            )}
            <button type="button" className="chat-ass-icona" aria-label={t('Chiudi')} onClick={() => setAperta(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </header>

          {passate ? (
            <div className="chat-ass-corpo">
              {passata ? (
                <>
                  {(passata.messaggi || []).map(fumetto)}
                  <div ref={fondo}/>
                </>
              ) : passate.length === 0 ? (
                <p className="chat-ass-vuoto">{t('Non hai ancora chat precedenti.')}</p>
              ) : (
                <ul className="chat-ass-elenco">
                  {passate.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => apriPassata(c.id)}>
                        <span className="chat-ass-elenco-data">{ora(c.apertaIl, i18n.language)}</span>
                        <span className="chat-ass-elenco-testo">{c.anteprima || '…'}</span>
                        <span className="chat-ass-elenco-stato">{c.stato === 'CHIUSA' ? t('Chiusa') : t('Aperta')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              <div className="chat-ass-corpo" aria-live="polite">
                {!conv && (
                  <div className="chat-ass-benvenuto">
                    <strong>{t('Ciao! Come possiamo aiutarti?')}</strong>
                    <span>{t('Scrivi la tua domanda: un operatore ti risponde qui appena possibile.')}</span>
                  </div>
                )}
                {messaggi.map(fumetto)}
                {senzaRisposta && <div className="chat-ass-sistema">{t('Ti risponderemo appena possibile.')}</div>}
                {chiusa && <div className="chat-ass-sistema">{t('Scrivi un nuovo messaggio per aprire un’altra chat.')}</div>}
                <div ref={fondo}/>
              </div>
              {errore && <div className="chat-ass-errore" role="alert">{errore}</div>}
              <form className="chat-ass-scrivi" onSubmit={manda}>
                <textarea ref={campo} rows={1} maxLength={TESTO_MAX} value={testo}
                          placeholder={t('Scrivi un messaggio')} aria-label={t('Scrivi un messaggio')}
                          onChange={(e) => setTesto(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); manda(); } }}/>
                <button type="submit" className="chat-ass-manda" disabled={!testo.trim() || invio} aria-label={t('Invia')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                  </svg>
                </button>
              </form>
            </>
          )}
        </section>
      )}

      <button type="button" className={'chat-ass-bolla' + (aperta ? ' aperta' : '')}
              aria-label={aperta ? t('Chiudi la chat') : t('Apri la chat di assistenza')}
              aria-expanded={aperta} onClick={() => { setAperta(!aperta); setPassate(null); setPassata(null); }}>
        {aperta ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
               strokeLinecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
        ) : (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12a8 8 0 0 1-11.8 7L4 20l1.1-4.4A8 8 0 1 1 21 12z"/>
            <path d="M8.5 11h.01M12 11h.01M15.5 11h.01"/>
          </svg>
        )}
        {!aperta && numero > 0 && <span className="chat-ass-numero">{numero > 9 ? '9+' : numero}</span>}
      </button>
    </div>
  );
}
